import { z } from 'zod';

import { COUNTRY_ISO3166_ALPHA3, US_STATES } from '@endaoment-frontend/constants';

import { percentageNumberSchema, timestampSchema, uuidSchema } from '../general';
import { addressSchema, bigIntSchema } from '../web3';

import { orgSimpleListingSchema } from './orgs';
import { physicalAddressSchema } from './user';

export const fundManagerSchema = z.object({
  id: uuidSchema,
  walletAddress: addressSchema,
});
export type FundManager = z.infer<typeof fundManagerSchema>;

export const fundPiiSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
});
export type FundPii = z.infer<typeof fundPiiSchema>;

/**
 * A field on a donor record as the API reports it: present, or `null` where the stored value was blank
 * or whitespace only. Everything downstream feeds a controlled text input, so the empty case becomes
 * `''` here rather than leaving each consumer to guard against a `null` that React would render as the
 * word "null".
 */
const donorTextSchema = z
  .string()
  .nullish()
  .transform(value => value ?? '');

/**
 * The address on a fund's donor-of-record.
 *
 * A well formed address parses through `physicalAddressSchema`, keeping the US state code narrowing.
 * Anything less complete falls through to the loose arm instead of failing to parse: a donor already
 * on file can be partial, and this data is only ever used to pre-fill a form that does its own
 * validation before submit. Rejecting a partial record here would silently hand the form back to the
 * acting user's own identity - exactly the mix-up this data exists to prevent.
 *
 * The loose arm never narrows a state on its own, so it mirrors the country-conditional shape of the
 * strict one: an address the form will show as USA whose state is not a `US_STATES` code is degraded
 * to '' rather than passed through as free text the state select cannot render and the identity schema
 * would later reject. "Will show as USA" has to include a missing or unparseable country, because the
 * form falls back to USA for those - leaving their free text alone would put an unselectable value in
 * the US state select and block the step on a field nobody touched.
 *
 * The country itself is left empty rather than rewritten to USA: only the state degrades. Guessing the
 * country here would render a foreign address as a plausible US one on a receipt.
 * @see physicalAddressSchema
 */
export const fundDonorAddressSchema = z.union([
  physicalAddressSchema,
  z
    .object({
      line1: donorTextSchema,
      line2: donorTextSchema,
      city: donorTextSchema,
      state: donorTextSchema,
      zip: donorTextSchema,
      // The odd one out: this drives a select of ISO-3166 alpha-3 codes, where '' is not an option
      country: COUNTRY_ISO3166_ALPHA3.nullish().catch(null),
    })
    .transform(address =>
      (address.country ?? COUNTRY_ISO3166_ALPHA3.enum.USA) === COUNTRY_ISO3166_ALPHA3.enum.USA &&
      !US_STATES.safeParse(address.state).success
        ? { ...address, state: '' }
        : address,
    ),
]);
export type FundDonorAddress = z.infer<typeof fundDonorAddressSchema>;

/**
 * Identifying details of the person a fund gives on behalf of, used to pre-fill the donor block of a
 * tax receipt. Only the fund's manager and its enabled collaborators are entitled to read it.
 *
 * Declared standalone rather than extending `fundPiiSchema` - the names on a donor record are nullable
 * where the ones on a fund's advisor are not, and widening the shared schema would loosen every fund
 * listing and collaborator that reads from it.
 */
export const fundDonorSchema = z.object({
  firstName: donorTextSchema,
  lastName: donorTextSchema,
  email: donorTextSchema,
  address: fundDonorAddressSchema,
});
export type FundDonor = z.infer<typeof fundDonorSchema>;
/**
 * The same record as the API puts it on the wire, before the schema normalizes it. Anything building a
 * donor payload - mocks, fixtures - should describe this shape rather than the parsed one.
 */
export type FundDonorResponse = z.input<typeof fundDonorSchema>;

export const fundCollaboratorSchema = fundPiiSchema.extend({
  id: uuidSchema,
  email: z.string().email(),
});
export type FundCollaborator = z.infer<typeof fundCollaboratorSchema>;

export const fundTypeSchema = z.enum(['Private', 'Community', 'Transparent', 'ImpactPool']);
export type FundType = z.infer<typeof fundTypeSchema>;

/**
 * Whether this is a fund many unrelated people give to, rather than the one household's fund it was
 * opened for.
 *
 * Anyone at all may give to these two, and they give their own money - so the person the fund itself
 * gives on behalf of, its donor of record, is not who a receipt for that gift belongs to. On the other
 * types a gift comes from the fund's own side, so the donor of record is the right person to name.
 *
 * This says nothing about who may *see* a fund, which is a separate setting: a Transparent fund is
 * viewable by anyone with a link and is still not open to outside donors.
 */
export const isOpenToPublicDonors = (type: FundType) => type === 'Community' || type === 'ImpactPool';

export const fundListingSchema = z.object({
  id: uuidSchema,
  chainId: z.number(),
  name: z.string(),
  type: fundTypeSchema,
  manager: fundManagerSchema,
  deploymentTransactionHash: addressSchema.nullish(),
  contractAddress: addressSchema.nullish(),
  advisor: fundPiiSchema,
  featuredIndex: z.number(),
  lastBalanceSyncUTC: z.date().nullish(),
  description: z.string(),
  shortDescription: z.string().nullish(),
  vanityUrl: z.string().nullish(),
  logo: z.string().nullish(),
  banner: z.string().nullish(),
  createdAtUtc: timestampSchema,
  updatedAtUtc: timestampSchema,
  category: z.string().nullish(),
  grantsGiven: z.number(),
  /** USDC value */
  usdcBalance: bigIntSchema.describe('Is represented as the Cash Balance in the UI'),
  /** USDC value */
  totalGrantedUsdc: bigIntSchema.describe('Is represented as the Total Granted Amount in the UI'),
  /** USDC value */
  lifetimeDonationsUsdc: bigIntSchema.describe('Is represented as the Lifetime Donations in the UI'),
  /** USDC value */
  investedUsdc: bigIntSchema.describe('Is represented as either Invested or Allocated in the UI'),
  /** A USDC value */
  inTransitBuyUsdcAmount: bigIntSchema,
  /** A USDC value */
  inTransitSellUsdcAmount: bigIntSchema,
  /** A USDC value */
  processingTransfersTotalUsdc: bigIntSchema,
  // TODO: Remove the nullish once coordinated with BE
  illiquidBalance: bigIntSchema.nullish(),

  /** Endaoment fees for inbound donations */
  inboundFeeBps: z.number(),
  /** Endaoment fees for outbound grants */
  outboundFeeBps: z.number(),
  /** Details about this fee schedule if a custom fee is setup */
  customFeeDetail: z.string().nullish(),

  // Visibility properties don't actually exist on the model, but are used to determine which fields to show in the UI
  positionsVisible: z.boolean({ coerce: true }),
  activityVisible: z.boolean({ coerce: true }),
  granteeVisible: z.boolean({ coerce: true }),
});
export type FundListing = z.infer<typeof fundListingSchema>;

const expectedDeploymentInfoSchema = z.object({
  expectedManagerAddress: addressSchema,
  expectedSalt: addressSchema,
  expectedComputedAddress: addressSchema,
});
const genericDetailsSchema = z.object({
  type: fundTypeSchema.exclude([fundTypeSchema.enum.ImpactPool]),
  poolDetails: z.null(),
  expectedDeploymentInfo: expectedDeploymentInfoSchema.nullish(),
});
const impactDetailsSchema = z.object(
  {
    type: z.literal(fundTypeSchema.enum.ImpactPool),
    poolDetails: z.object({
      eligibleEntities: z.enum(['Organizations']),
      algorithmType: z.enum(['QuadraticFunding']),
      distributionSchedule: z.string(),
      distributionPercentage: z.number().describe('A percentage number between 0 and 1'),
      eligibleVotes: z.enum(['GrantsAndDonations']),
      voteWeight: z.enum(['UsdcValue']),
    }),
    expectedDeploymentInfo: expectedDeploymentInfoSchema.nullish(),
  },
  {
    errorMap: () => ({
      message: 'Impact Pool details are required for Impact Pools',
    }),
  },
);

export const fundDetailsSchema = z.union([
  fundListingSchema.merge(genericDetailsSchema),
  fundListingSchema.merge(impactDetailsSchema),
]);
export type FundDetails = z.infer<typeof fundDetailsSchema>;

export const fundDistributionRoundSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  roundStartTimestampUtc: timestampSchema.describe('Timestamp of round start'),
  roundEndTimestampUtc: timestampSchema.describe('Timestamp of round end'),
  distributedTimestampUtc: timestampSchema.nullish().describe('Timestamp of distribution processing'),
});
export type FundDistributionRound = z.infer<typeof fundDistributionRoundSchema>;

export const fundDistributionRoundSummarySchema = z.object({
  roundStartTimestampUtc: timestampSchema.describe('Timestamp of round start'),
  roundEndTimestampUtc: timestampSchema.describe('Timestamp of round end'),
  distributedTimestampUtc: timestampSchema.nullish().describe('Timestamp of distribution processing'),
  totalDistributionUsdc: bigIntSchema,
  impactPoolNav: bigIntSchema,
  distributionPercentage: percentageNumberSchema,
  topMajorCategories: z.array(
    z.object({
      nteeCode: z.string(),
      amount: bigIntSchema,
    }),
  ),
  recipientCount: z.number(),
});
export type FundDistributionRoundSummary = z.infer<typeof fundDistributionRoundSummarySchema>;

export const fundDistributionRecipientSchema = orgSimpleListingSchema.and(
  z.object({
    distributionAmount: bigIntSchema,
  }),
);
export type FundDistributionRecipient = z.infer<typeof fundDistributionRecipientSchema>;
